import React, { useRef, useState, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { Button, Container, Icon, hooks } from 'roadie-ui'
import { PanelTitle } from 'components/layout/Panel'
import { SenderRecognitions } from 'data/senderRecognitions'
import { RecognitionTile } from './RecognitionTile'
import SenderRecognitionsInfoModal from './SenderRecognitionsInfoModal'
import { useLocalization, LocalizedText } from 'localization'

const TilesScrollContainer = styled(Container.Flex)`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar across browsers */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const TILE_GAP = '2rem'

const PageDot = styled.button<{ $active: boolean }>`
  padding: 0.6rem;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;

  &::after {
    content: '';
    display: block;
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 50%;
    background: ${(props) => (props.$active ? props.theme.colors.greyDark : props.theme.colors.greyLight)};
  }
`

function numPages(totalTiles: number, tilesPerPage: number): number {
  return Math.ceil(totalTiles / tilesPerPage)
}

export const SenderRecognitionsSection: React.FC = () => {
  const { t } = useLocalization()
  const senderRecognitions = useSelector<any, SenderRecognitions>((state) => state.senderRecognitions)
  const isWebview = useSelector<any, boolean>((state) => state.config.isWebview)

  const breakpointTilesPerPage = hooks.useBreakPoints({ mobile: 3, tablet: 4, desktop: 4, ultrawide: 4 }) ?? 3
  const tilesPerPage = isWebview ? 3 : breakpointTilesPerPage

  const scrollRef = useRef<HTMLDivElement>(null)
  const tileRefs = useRef<(HTMLDivElement | null)[]>([])
  const [activePage, setActivePage] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)

  const scrollToPage = useCallback(
    (page: number) => {
      const tileIndex = page * tilesPerPage
      const tileEl = tileRefs.current[tileIndex]
      if (!tileEl) return

      tileEl.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
      setActivePage(page)
    },
    [tilesPerPage]
  )

  const recognitionCount = senderRecognitions.recognitions.length
  const pages = numPages(recognitionCount, tilesPerPage)

  useEffect(() => {
    setActivePage((current) => Math.min(current, Math.max(pages - 1, 0)))
  }, [pages])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth || 1)
    setActivePage(Math.round(scrollRatio * (pages - 1)))
  }, [pages])

  if (!senderRecognitions.showSection || recognitionCount === 0) {
    return null
  }

  return (
    <Container.Base $width="100%" $minWidth="0" $overflow="hidden">
      <Container.Grid $gridTemplateColumns="1fr max-content" $alignItems="center">
        <PanelTitle>
          <LocalizedText translationKey="dweb_dashboard_sender_recognitions_info_modal_sender_recognitions" />
        </PanelTitle>
        <Button.Tertiary
          type="button"
          onClick={() => setModalOpen(true)}
          aria-label={t('dweb_dashboard_sender_recognitions_section_about_sender_recognitions')}
        >
          <Icon.QuestionMarkCircle $color="neutral-base" $width="2.4rem" $height="2.4rem" />
        </Button.Tertiary>
      </Container.Grid>
      <Container.Panel $padding="0" $paddingMedia={{ mobile: '0' }}>
        <Container.Base $padding={pages > 1 ? `2rem ${TILE_GAP} 0` : `2rem ${TILE_GAP} 2rem`}>
          <TilesScrollContainer ref={scrollRef} onScroll={handleScroll} $gap={TILE_GAP} $flexWrap="nowrap">
            {senderRecognitions.recognitions.map((recognition, index) => (
              <Container.FlexItem
                key={recognition.key}
                ref={(el) => {
                  tileRefs.current[index] = el
                }}
                $flexGrow="0"
                $flexShrink="0"
                $flexBasis="auto"
                style={{ scrollSnapAlign: 'start' }}
              >
                <RecognitionTile recognitionKey={recognition.key} title={recognition.title} count={recognition.count} />
              </Container.FlexItem>
            ))}
          </TilesScrollContainer>
        </Container.Base>
        {pages > 1 && (
          <Container.Base $padding={`2rem 0 2rem`}>
            <Container.Flex $justifyContent="center" $gap="0.6rem">
              {Array.from({ length: pages }, (_, i) => (
                <PageDot
                  key={i}
                  type="button"
                  $active={i === activePage}
                  onClick={() => scrollToPage(i)}
                  aria-label={`View recognition page ${i + 1}`}
                  aria-current={i === activePage ? 'true' : undefined}
                />
              ))}
            </Container.Flex>
          </Container.Base>
        )}
      </Container.Panel>
      <SenderRecognitionsInfoModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </Container.Base>
  )
}
